import classNames from "classnames";
import { Tabs } from "..";
import { NumberCard } from "../../../../../components/cards/numberCard";
import { Icon } from "../../../../../components/icon/icon";
import { SwitchInput } from "../../../../../components/inputs/switch";
import { MiniHeader } from "../../../../../components/typography/miniHeader";
import { RenderRichText } from "../../../../../components/typography/renderRichText";
import { BgWrapper } from "../../../../../components/wrapper/bgWrapper";
import { useAdminUpdateEventTrendingMutation } from "../../../../../redux/features/admin/event/events.api";
import { useAdminGetPurchaseStatisticsQuery } from "../../../../../redux/features/admin/payments/payments.api";
import {
  CreatedEvent,
  GetEventGuestsResponse,
  GetEventTicketsResponse,
} from "../../../../../redux/features/event/event.types";
import { PayoutCurrency } from "../../../../../redux/features/payment/payment.types";
import { HostCard } from "../../../../../components/cards/hostCard";

export const AdminEventOverview = ({
  event,
  setTab,
  ticketsData,
  guests,
  eventCurrency,
  loading = false,
  refetchEventDetails = () => {},
}: {
  event: CreatedEvent;
  setTab: (tab: Tabs) => void;
  ticketsData?: GetEventTicketsResponse;
  guests?: GetEventGuestsResponse;
  eventCurrency?: PayoutCurrency;
  loading?: boolean;
  refetchEventDetails?: () => void;
}) => {
  const [updateTrending, { isLoading }] = useAdminUpdateEventTrendingMutation();

  const { data: statistics, isFetching: fetchingStatistics } =
    useAdminGetPurchaseStatisticsQuery(
      {
        eventId: event._id,
      },
      { refetchOnMountOrArgChange: true },
    );

  return (
    <>
      <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
        <div className="grid grid-cols-1 gap-[10px] md:grid-cols-3">
          <NumberCard
            value={0}
            text="Views"
            isCurrency={false}
            tooltipId={undefined}
            tooltipText={undefined}
            loading={fetchingStatistics}
          />

          <NumberCard
            value={guests?.data?.totalCount ?? 0}
            text="Guests"
            isCurrency={false}
            tooltipId={undefined}
            tooltipText={undefined}
            loading={fetchingStatistics}
          />

          <NumberCard
            value={ticketsData?.data?.totalCount ?? 0}
            text="Event Tickets"
            isCurrency={false}
            tooltipId={undefined}
            tooltipText={undefined}
            loading={fetchingStatistics}
          />

          <NumberCard
            value={statistics?.data.totalTicketsSold ?? 0}
            text="Total Tickets Sold"
            isCurrency={false}
            tooltipId={"totalTicketsSold"}
            tooltipText={"The total number of tickets that have been bought."}
            loading={fetchingStatistics}
          />

          <NumberCard
            value={statistics?.data.totalSalesRevenue ?? 0}
            text="Total Sales Revenue"
            tooltipId={"totalSalesRevenue"}
            tooltipText={
              "The total amount of tickets sold which includes our fees."
            }
            loading={fetchingStatistics}
            currency={eventCurrency}
          />

          <NumberCard
            value={statistics?.data.totalAmountMade ?? 0}
            text="Total Amount Made"
            tooltipId={"totalAmountMade"}
            tooltipText={
              "You'll receive this amount in your payout account—your earnings from ticket sales after our fees have been deducted."
            }
            loading={fetchingStatistics}
            currency={eventCurrency}
          />
        </div>
      </BgWrapper>

      <div className="grid gap-2 lg:grid-cols-2">
        <BgWrapper
          className={classNames("flex flex-col gap-3")}
          loading={loading}
          loaderHeight={120}
        >
          <MiniHeader header="Description" className="!text-grey-500" />

          <div className="text-sm font-bold text-grey-900">
            <RenderRichText>{event?.description ?? ""}</RenderRichText>
          </div>
        </BgWrapper>

        <div className="flex flex-col gap-2">
          {event.visibility !== "private" && (
            <BgWrapper
              className={classNames("flex flex-col gap-1")}
              loading={loading}
              loaderHeight={76}
            >
              <MiniHeader header="Trending" className="!text-grey-500" />

              <div className="flex items-center justify-between gap-2 text-base font-bold text-grey-900">
                <p>Set as trending event 🔥</p>
                <SwitchInput
                  checked={event.isTrending}
                  disabled={event.status !== "upcoming" || isLoading}
                  onChange={async () => {
                    const resp = await updateTrending({
                      id: event._id,
                      isTrending: !event.isTrending,
                    });

                    if (resp.data?.status === "success") {
                      refetchEventDetails();
                    }
                  }}
                  className={""}
                />
              </div>
            </BgWrapper>
          )}
          <BgWrapper
            className={classNames("flex flex-col gap-1")}
            loading={loading}
            loaderHeight={76}
          >
            <MiniHeader header="Event Visibility" className="!text-grey-500" />

            <div className="flex items-center gap-2 text-base font-bold text-grey-900">
              <p>
                {event.visibility === "private"
                  ? "Private Event"
                  : "Public Event"}
              </p>

              <Icon
                svg={event.visibility === "private" ? "padlock" : "globe"}
                width={16}
                height={16}
                className="text-grey-500"
              />
            </div>
          </BgWrapper>
          <BgWrapper
            className={classNames("flex flex-col gap-1")}
            loading={loading}
            loaderHeight={76}
          >
            <MiniHeader header="Category" className="!text-grey-500" />

            <div className="text-base font-bold text-grey-900">
              {event.category}
            </div>
          </BgWrapper>

          <BgWrapper
            className={classNames("flex flex-col gap-1")}
            loading={loading}
            loaderHeight={120}
          >
            <MiniHeader header="Who Pays The Fees" className="!text-grey-500" />

            <div className="text-base font-bold text-grey-900">
              {event.feePayer === "guest" ? "Guests" : "Organizer"}
            </div>
          </BgWrapper>
          <BgWrapper
            className="flex flex-col gap-3"
            loading={loading}
            loaderHeight={80}
          >
            <MiniHeader header="Hosted By" className="!text-grey-500" />
            <div className="flex flex-col gap-2">
              <HostCard
                user={event?.createdBy}
                role="CREATOR"
                roleVariation="purple"
              />
              {event.coOrganizers
                ?.filter((coOrg) => coOrg?.status === "accepted")
                .map((coOrg) => (
                  <HostCard
                    key={coOrg?.userId?._id}
                    user={coOrg?.userId}
                    role={
                      coOrg.role === "manager" ? "MANAGER" : "CHECK-IN ONLY"
                    }
                    roleVariation="blue"
                  />
                ))}
            </div>
          </BgWrapper>
        </div>
      </div>
    </>
  );
};
